import { Alert, Button, Stack } from "@chakra-ui/react";
import { Wallet } from "lucide-react";
import { formatSettlement } from "../../mappers/payouts.mapper";
import { isConfirmingPayment } from "../../hooks/useFetchBatchTopUps";
import { statusMessagesMapping } from "@/constants/paymentStatusMessages";
import type { PayoutBatch, TopUp } from "../../types/payouts.type";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(value);

interface BatchFundingPanelProps {
  batch: PayoutBatch;
  shortfall: number;
  latest?: TopUp;
  approved?: TopUp;
  canFund: boolean;
  onFund: () => void;
}

export default function BatchFundingPanel({
  batch,
  shortfall,
  latest,
  approved,
  canFund,
  onFund,
}: BatchFundingPanelProps) {
  const fundButton = canFund && (
    <Button size="sm" colorPalette="palomma" onClick={onFund} flexShrink={0}>
      <Wallet size={16} /> Cargar fondos
    </Button>
  );

  if (shortfall <= 0)
    return (
      <Alert.Root status="success" colorPalette="green">
        <Alert.Indicator />
        <Alert.Title fontSize="sm">
          Ya tienes fondos suficientes para dispersar este lote.
        </Alert.Title>
      </Alert.Root>
    );

  if (approved)
    return (
      <Alert.Root status="info" colorPalette="blue">
        <Alert.Indicator />
        <Alert.Title fontSize="sm">
          Fondos en camino: {formatCurrency(approved.amount - approved.fee)} se
          acreditan {formatSettlement(approved)} y el lote se dispersa
          automáticamente.
        </Alert.Title>
      </Alert.Root>
    );

  if (batch.status === "funding")
    return (
      <Alert.Root status="info" colorPalette="blue">
        <Alert.Indicator />
        <Alert.Title fontSize="sm">
          Fondos en camino. El lote se dispersa automáticamente cuando se
          acrediten.
        </Alert.Title>
      </Alert.Root>
    );

  const confirming = isConfirmingPayment(latest);
  const rejected = latest?.status === "cancelled";

  return (
    <Alert.Root
      status={confirming ? "info" : "warning"}
      colorPalette={confirming ? "blue" : "orange"}
    >
      <Alert.Indicator />
      <Stack
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        gap={3}
        flex={1}
      >
        <Alert.Title fontSize="sm">
          {confirming
            ? "Estamos confirmando tu pago con el banco. Puede tardar unos minutos."
            : rejected
            ? `Tu último pago no se completó${
                latest.statusMessage
                  ? ` (${statusMessagesMapping[latest.statusMessage]})`
                  : ""
              }. Faltan ${formatCurrency(shortfall)} para dispersar este lote.`
            : `Faltan ${formatCurrency(shortfall)} para dispersar este lote.`}
        </Alert.Title>
        {!confirming && fundButton}
      </Stack>
    </Alert.Root>
  );
}
